import {ReactElement} from 'react'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import Jdenticon from 'react-jdenticon'
import {Button, Grid, Paper, Typography} from '@material-ui/core'
import {accountSubtypes, accountTypes, CompanyEnumIndices, IAccountSubtypeEnum, KycStatusEnumIndices} from '@bdswiss/common-enums'
import {formatDate, formatPercent, isMobile} from '../../../../../../../common/utils'
import {AccountTypeTooltip} from '../../../../components/AccountTypeTooltip'
import messages from '../../../../../../../assets/messages'
import {Account, Client, BrokereeCopyTradingOffer} from '../../../../types'
import {Statistics} from './components/Statistics'
import {useStyles} from './styles'

type OfferCardPropsBase = {
  client: Client
  account: Account
  offer: BrokereeCopyTradingOffer
  hasActiveSubscription: boolean
  hasOpenPositions: boolean
  onSubscribeRequest: (offer: BrokereeCopyTradingOffer) => void
  onUnsubscribeRequest: (offer: BrokereeCopyTradingOffer) => void
}

type OfferCardProps = OfferCardPropsBase & WithNamespaces

function isValidKyc(client: Client, account: Account): boolean {
  const accountCategory = accountTypes[account.__typename ?? '']?.category
  switch (accountCategory) {
      case 'forexMt5': {
        return (
          // we don't check it for SVG intentionally PT-4524
          client.company === CompanyEnumIndices.tauroMarkets || client.kycStatus === KycStatusEnumIndices.approved
        )
      }
      case 'forexMt4': {
        return true // we don't check it for MT4 intentionally
      }
      default: {
        return false
      }
    }
}

const Component = (props: OfferCardProps): ReactElement => {
  const {lng, offer, client, account, hasActiveSubscription, hasOpenPositions, onSubscribeRequest, onUnsubscribeRequest} = props
  const {id, name, isSubscribed, isUnsubscribePending, performanceFee, provider, statistics} = offer
  const {startedAt, account: providerAccount} = provider

  const classes = useStyles()

  const canSubscribe = !hasOpenPositions
                    && !hasActiveSubscription
                    && !account.isReadOnly
                    && !account.isViewOnly
                    && !isSubscribed
                    && isValidKyc(client, account)

  const canUnsubscribe = isSubscribed && !isUnsubscribePending

  const accountSubtype = accountSubtypes[providerAccount.subtype] as IAccountSubtypeEnum | undefined

  const handleSubscribeRequest = () => {
    if (!canSubscribe) return
    onSubscribeRequest(offer)
  }

  const handleUnsubscribeRequest = () => {
    if (!canUnsubscribe) return
    onUnsubscribeRequest(offer)
  }

  return (
    <Grid item container xs={12}>
      <Paper className={classes.container}>
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item container direction='row' alignItems='center' wrap='nowrap' justifyContent='center' xs={12} md={4} lg={3}>
            <Grid item className={classes.strategyIcon}>
              <Jdenticon size="56" value={String(id)}/>
            </Grid>
            <Grid item container xs className={classes.strategyDescription}>
              <Grid item container xs className={classes.nameSection}>
                <Typography variant='body1' className={classes.descriptionTitle}>
                  {name}
                </Typography>
                <AccountTypeTooltip accountSubtype={accountSubtype?.label ?? null} />
              </Grid>
              <Typography variant='caption' className={classes.descriptionCaption}>
                {formatPercent(lng, performanceFee)} <Trans {...messages.performanceFee} />
              </Typography>
              <Typography variant='caption' className={classes.descriptionCaption}>
                <Trans {...messages.activeSince} /> {formatDate(lng, startedAt)}
              </Typography>
            </Grid>
          </Grid>
          <Statistics statistics={statistics} />
          <Grid item className={classes.controls}>
            {isSubscribed ? (
              <>
                {isUnsubscribePending ?
                  <Typography variant='caption'><Trans {...messages.pending} /></Typography>
                  : <Typography variant='caption' className={classes.copying}><Trans {...messages.copying} /></Typography>
                }
                <Button
                  color={!isMobile() ? 'secondary' : 'primary'}
                  variant={!isMobile() ? 'contained' : 'text'}
                  size="medium"
                  disabled={!canUnsubscribe}
                  onClick={handleUnsubscribeRequest}
                >
                  <Trans {...messages.stopCopying}/>
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant={!isMobile() ? 'contained' : 'text'}
                  size="medium"
                  disabled={!canSubscribe}
                  onClick={handleSubscribeRequest}
                >
                  <Trans {...messages.startCopying}/>
                </Button>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}

export const OfferCard = withNamespaces()(Component)
